// src/evaluations/EvaluationComparison.js
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  FileDownload as ExportIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import useStore from "../config/store";
import api from '../config/api';
import EvaluationComparisonTabs from "./EvaluationComparisonTabs";
// Modo simple: sin psicolaboral ni Mapa de Talentos (PLAN_UNIFICACION_ASSESSMENT.md)
import EvaluationComparisonTabsSimple from "./EvaluationComparisonTabsSimple";
import { getAssessmentMode, isSimpleAssessment } from "./assessmentMode";
import { useTranslation } from "react-i18next";

// Componentes estilizados
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[8],
  },
}));

const EvaluationComparison = ({
  evaluation,
  onBack,
  onBackToList,
  onBackToEdit,
}) => {
  // Estados principales
  const [candidatesData, setCandidatesData] = useState([]);
  const [competenciesData, setCompetenciesData] = useState([]);
  const [evaluationResponses, setEvaluationResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados calculados
  const [metrics, setMetrics] = useState({
    totalCandidates: 0,
    completedCandidates: 0,
    averageScore: 0,
    bestCompetency: { name: "", score: 0 },
    worstCompetency: { name: "", score: 0 },
  });

  const { authToken, language } = useStore();
  const { t } = useTranslation();

  // Cargar datos al montar el componente
  useEffect(() => {
    if (evaluation) {
      loadEvaluationData();
    }
  }, [evaluation]);

  const loadEvaluationData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Obtener la estructura de la evaluación
      const evaluationResponse = await api.get(
        `/evaluations/${evaluation.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (
        evaluationResponse.data.errorCode !== 0 ||
        !evaluationResponse.data.response?.data
      ) {
        throw new Error("No se pudo obtener la evaluación");
      }

      const evaluationData = evaluationResponse.data.response.data;

      // 2. Obtener evaluationResponses usando la URL que funciona
      const responsesResult = await api.get(
        `/evaluation-responses/by-evaluation?evaluationId=${encodeURIComponent(
          evaluation.id
        )}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (
        responsesResult.data.errorCode !== 0 ||
        !responsesResult.data.response?.data
      ) {
        throw new Error("No se pudieron obtener las evaluationResponses");
      }

      const responses = responsesResult.data.response.data;

      setEvaluationResponses(responses);

      // 3. Procesar datos con scores reales
      const applicants = evaluationData.applicants || [];
      const processedCandidates = processCompleteCandidatesData(
        applicants,
        responses,
        evaluationData
      );
      setCandidatesData(processedCandidates);

      const processedCompetencies = processCompetenciesWithScores(
        evaluationData.capabilities,
        responses,
        processedCandidates
      );
      setCompetenciesData(processedCompetencies);

      const calculatedMetrics = calculateComprehensiveMetrics(
        processedCandidates,
        processedCompetencies
      );
      setMetrics(calculatedMetrics);
    } catch (err) {
      console.error("Error cargando datos de evaluación:", err);
      setError(`Error al cargar los datos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const processCompleteCandidatesData = (
    applicants,
    responses,
    evaluationData
  ) => {
    return applicants.map((applicant) => {
      // Buscar response correspondiente
      const response = responses.find(
        (r) => r.applicantId === applicant.applicantId
      );

      // Convertir timestamps de Firebase
      const convertTimestamp = (ts) =>
        ts?._seconds ? new Date(ts._seconds * 1000) : null;

      let completedAt = null;
      let startedAt = null;
      let timeSpent = 0;
      let overallScore = 0;
      let reportOverallScore = 0;
      let competencyScores = {};
      let completedQuestions = 0;
      let status = applicant.status || "pending";

      if (response) {
        // Usar datos de la response si existe
        completedAt = convertTimestamp(response.completedAt);
        startedAt = convertTimestamp(response.startedAt);
        // CORREGIDO: convertir de segundos a minutos
        timeSpent = response.totalTimeSpent
          ? Math.round(response.totalTimeSpent / 60)
          : 0;
        overallScore = response.overallScore || 0;
        reportOverallScore = response.reportOverallScore || 0;
        status = response.overallStatus || status;

        if (response.questions) {
          const questions = response.questions;
          completedQuestions = questions.filter(
            (q) => q.answerText && q.answerText.trim()
          ).length;

          // Scores oficiales por competencia (raíz del documento): misma fuente
          // que el detalle y los reportes, ya penalizados por las preguntas
          // sin responder.
          const rootScores = Array.isArray(response.competencyScores)
            ? response.competencyScores
            : [];

          // Agrupar por competencia usando los IDs de la response
          evaluationData.capabilities?.forEach((capability) => {
            const rootMatch = rootScores.find(
              (rs) => rs.competencyId === capability.capabilityId
            );

            if (rootMatch && typeof rootMatch.competencyScore === "number") {
              competencyScores[capability.capabilityId] = rootMatch.competencyScore;
              return;
            }

            // Fallback para assessments antiguos sin competencyScores en la raíz
            const capabilityQuestions = questions.filter(
              (q) => q.competencyId === capability.capabilityId
            );
            if (capabilityQuestions.length > 0) {
              const scores = capabilityQuestions
                .map((q) => q.evaluationScore)
                .filter((score) => score !== null && score !== undefined);

              if (scores.length > 0) {
                const avgScore =
                  scores.reduce((sum, score) => sum + Number(score), 0) /
                  scores.length;
                competencyScores[capability.capabilityId] = avgScore;
              }
            }
          });
        }
      } else {
        // Fallback a datos del applicant si no hay response
        completedAt = convertTimestamp(applicant.completedAt);
        startedAt = convertTimestamp(applicant.startedAt);

        if (completedAt && startedAt) {
          // Este cálculo ya estaba correcto (convierte de milisegundos a minutos)
          timeSpent = Math.round((completedAt - startedAt) / (1000 * 60));
        }
      }

      return {
        id: applicant.applicantId,
        name: applicant.name || t("evaluationRespones.noName"),
        email: applicant.email || "",
        status: status,
        overallScore: overallScore,
        reportOverallScore: reportOverallScore,
        competencyScores: competencyScores,
        completedQuestions: completedQuestions,
        totalQuestions:
          evaluationData.capabilities?.reduce(
            (sum, cap) => sum + (cap.questions?.length || 0),
            0
          ) || 0,
        progress: status === "completed" ? 100 : status === "started" ? 50 : 0,
        timeSpent: timeSpent, // Ahora en minutos correctamente
        submittedAt: completedAt,
        position:
          evaluationData.positionName || t("evaluationResponses.noPosition"),
        hasResponse: !!response,
      };
    });
  };

  const processCompetenciesWithScores = (
    capabilities,
    responses,
    candidatesData
  ) => {
    return (capabilities || []).map((capability) => {
      console.log(`\n=== COMPETENCIA: ${capability.capabilityName || capability.name} ===`);

      // Usar directamente candidatesData en lugar de iterar responses
      const candidateScores = [];

      candidatesData.forEach((candidate) => {
        const score = candidate.competencyScores[capability.capabilityId];
        console.log(`${candidate.name}: ${score}`);

        if (score !== null && score !== undefined) {
          candidateScores.push(Number(score));
        }
      });

      const averageScore = candidateScores.length > 0
        ? candidateScores.reduce((sum, score) => sum + score, 0) / candidateScores.length
        : 0;

      return {
        id: capability.capabilityId,
        name: capability.capabilityName || capability.name,
        averageScore: averageScore,
        weight: capability.weight || 1,
        candidateScores: candidatesData.map((candidate) => ({
          candidateId: candidate.id,
          candidateName: candidate.name,
          score: candidate.competencyScores[capability.capabilityId] || 0,
        })),
        totalResponses: candidateScores.length,
      };
    });
  };

  const calculateComprehensiveMetrics = (candidatesData, competenciesData) => {
    const totalCandidates = candidatesData.length;
    const completedCandidates = candidatesData.filter(
      (c) => c.status === "completed"
    ).length;

    // Candidatos completados con response (incluir scores de 0 o mayores)
    const candidatesWithValidScores = candidatesData.filter(
      (c) => c.status === "completed" && c.hasResponse && c.overallScore >= 0
    );

    const averageScore =
      candidatesWithValidScores.length > 0
        ? candidatesWithValidScores.reduce(
          (sum, candidate) => sum + candidate.overallScore,
          0
        ) / candidatesWithValidScores.length
        : 0;

    // Encontrar mejor y peor competencia basándose en scores reales
    let bestCompetency = { name: "N/A", score: 0 };
    let worstCompetency = { name: "N/A", score: 0 };

    const competenciesWithScores = competenciesData.filter(
      (c) => c.averageScore >= 0 && c.totalResponses > 0
    );

    if (competenciesWithScores.length > 0) {
      const sortedCompetencies = [...competenciesWithScores].sort(
        (a, b) => b.averageScore - a.averageScore
      );
      bestCompetency = {
        name: sortedCompetencies[0].name,
        score: sortedCompetencies[0].averageScore,
      };

      // Si hay más de una competencia, mostrar la peor
      if (sortedCompetencies.length > 1) {
        worstCompetency = {
          name: sortedCompetencies[sortedCompetencies.length - 1].name,
          score: sortedCompetencies[sortedCompetencies.length - 1].averageScore,
        };
      } else {
        // Si solo hay una competencia, esa misma es la que necesita mejora
        worstCompetency = {
          name: bestCompetency.name,
          score: bestCompetency.score,
        };
      }
    }

    return {
      totalCandidates,
      completedCandidates,
      averageScore,
      bestCompetency,
      worstCompetency,
    };
  };

  const getScoreColor = (score) => {
    if (score >= 85) return "success";
    if (score >= 70) return "warning";
    return "error";
  };

  const handleExportWord = async () => {
    try {
      console.log(t("evaluationResponses.export.startingBackendExport"));

      // Obtener projectId y organizationId del store
      const { project: projectId, organization: organizationId } =
        useStore.getState();

      if (!projectId || !organizationId) {
        alert(t("evaluationResponses.export.missingProjectData"));
        return;
      }

      // Preparar datos para enviar al backend
      const exportData = {
        evaluation: {
          title: evaluation.title,
          positionName: evaluation.positionName,
          mode: getAssessmentMode(evaluation),
        },
        metrics,
        candidatesData,
        competenciesData,
        evaluationResponses,
      };

      // Realizar llamada al backend
      const response = await api.post(
        `/evaluation-responses/export-word`,
        exportData,
        {
          params: {
            projectId,
            organizationId,
            language,
          },
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          responseType: "blob", // Importante para recibir el archivo
        }
      );

      // Crear nombre del archivo
      const fileName = `${evaluation.title.replace(
        /\s+/g,
        "_"
      )}_${new Date().getTime()}.docx`;

      // Crear URL para descarga
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const url = URL.createObjectURL(blob);

      // Crear elemento de descarga
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      // Limpiar
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(t("evaluationResponses.export.downloadSuccessful"));
    } catch (error) {
      console.error(t("evaluationResponses.export.backendExportError"), error);

      let errorMessage = t("evaluationResponses.export.error");

      if (error.response) {
        // Error del servidor
        if (error.response.status === 400) {
          errorMessage = t("evaluationResponses.export.incompleteData");
        } else if (error.response.status === 500) {
          errorMessage = t("evaluationResponses.export.serverError");
        }
      } else if (error.request) {
        // Error de red
        errorMessage = t("evaluationResponses.export.connectionError");
      }

      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {t("evaluationResponses.loadingEvaluationData")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          {t("evaluationResponses.backToEvaluations")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={onBackToList || onBack}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <HomeIcon fontSize="small" />
          {t("evaluationResponses.evaluations")}
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={onBackToEdit || onBack}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          {evaluation.title}
        </Link>
        <Typography color="text.primary">
          {t("evaluationResponses.comparativeDashboard")}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t("evaluationResponses.comparativeDashboard")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {evaluation.title} - {evaluation.positionName}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<ExportIcon />}
            variant="outlined"
            onClick={handleExportWord}
          >
            {t("evaluationResponses.exportWord")}
          </Button>
        </Box>
      </Box>

      {/* Métricas principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: "center" }}>
              <PeopleIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography variant="h4" component="div" gutterBottom>
                {metrics.totalCandidates}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("evaluationResponses.totalApplicants")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("evaluationResponses.applicantsBreakdown", {
                  qualified: candidatesData.filter(
                    (c) => c.status === "completed" && c.hasResponse
                  ).length,
                  unqualified: candidatesData.filter(
                    (c) => c.status === "completed" && !c.hasResponse
                  ).length,
                  inProgress: candidatesData.filter(
                    (c) => c.status !== "completed"
                  ).length,
                })}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: "center" }}>
              <AssessmentIcon
                sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
              />
              <Typography variant="h4" component="div" gutterBottom>
                {metrics.averageScore.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("evaluationResponses.averageScore")}
              </Typography>
              <Box sx={{ mt: 1, mx: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={metrics.averageScore}
                  sx={{ height: 6, borderRadius: 3 }}
                  color={getScoreColor(metrics.averageScore)}
                />
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: "center" }}>
              <TrendingUpIcon
                sx={{ fontSize: 40, color: "success.main", mb: 1 }}
              />
              <Typography variant="h6" component="div" gutterBottom>
                {t("evaluationResponses.bestCompetency")}
              </Typography>
              <Typography variant="h5" color="success.main" gutterBottom>
                {metrics.bestCompetency.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("evaluationResponses.averageWithValue", {
                  value: metrics.bestCompetency.score.toFixed(1),
                })}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: "center" }}>
              <TimelineIcon
                sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
              />
              <Typography variant="h6" component="div" gutterBottom>
                {t("evaluationResponses.competencyToImprove")}
              </Typography>
              <Typography variant="h5" color="warning.main" gutterBottom>
                {metrics.worstCompetency.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("evaluationResponses.averageWithValue", {
                  value: metrics.worstCompetency.score.toFixed(1),
                })}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
      {/* Tabs de contenido */}
      {isSimpleAssessment(evaluation) ? (
        <EvaluationComparisonTabsSimple
          candidatesData={candidatesData}
          competenciesData={competenciesData}
          evaluationResponses={evaluationResponses}
        />
      ) : (
        <EvaluationComparisonTabs
          candidatesData={candidatesData}
          competenciesData={competenciesData}
          evaluationResponses={evaluationResponses}
        />
      )}
    </Box>
  );
};

export default EvaluationComparison;
